/**
 * Appointment Actions Hook (New Backend)
 * Complete appointment FSM operations via REST endpoints.
 * 
 * Appointment FSM States: pending → approved → confirmed → in_progress → completed
 * Alternative paths: cancelled, no_show
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import apiClient from '@/api/client'
import { dashboardKeys } from '@/hooks/useDashboardStats'

// ============================================================================
// Types
// ============================================================================

export interface AppointmentActionResult<T = unknown> {
  success: boolean
  message?: string
  appointment?: T
}

export interface UpdateAppointmentInput {
  scheduled_start?: string
  scheduled_end?: string
  notes?: string
  location_address?: string
  location_city?: string
}

// ============================================================================
// API Functions
// ============================================================================

async function requestApproval(appointmentId: string): Promise<AppointmentActionResult> {
  const resp = await apiClient.post<AppointmentActionResult>(
    `/api/v1/orders/appointments/${appointmentId}/request-approval/`
  )
  return resp.data
}

async function approveAppointment(appointmentId: string): Promise<AppointmentActionResult> {
  const resp = await apiClient.post<AppointmentActionResult>(
    `/api/v1/orders/appointments/${appointmentId}/approve/`
  )
  return resp.data
}

async function confirmAppointment(appointmentId: string): Promise<AppointmentActionResult> {
  const resp = await apiClient.post<AppointmentActionResult>(
    `/api/v1/orders/appointments/${appointmentId}/confirm/`
  )
  return resp.data
}

async function cancelAppointment(appointmentId: string, reason?: string): Promise<AppointmentActionResult> {
  const resp = await apiClient.post<AppointmentActionResult>(
    `/api/v1/orders/appointments/${appointmentId}/cancel/`,
    reason ? { reason } : undefined
  )
  return resp.data
}

async function markNoShow(appointmentId: string, reason?: string): Promise<AppointmentActionResult> {
  const resp = await apiClient.post<AppointmentActionResult>(
    `/api/v1/orders/appointments/${appointmentId}/mark-no-show/`,
    reason ? { reason } : undefined
  )
  return resp.data
}

async function updateAppointment(
  appointmentId: string,
  input: UpdateAppointmentInput
): Promise<AppointmentActionResult> {
  const resp = await apiClient.patch<AppointmentActionResult>(
    `/api/v1/orders/appointments/${appointmentId}/`,
    input
  )
  return resp.data
}

// ============================================================================
// Hook
// ============================================================================

export function useAppointmentActions() {
  const queryClient = useQueryClient()

  const invalidateAll = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all }),
      queryClient.invalidateQueries({ queryKey: ['orders'] }),
      queryClient.invalidateQueries({ queryKey: ['order'] }),
      queryClient.invalidateQueries({ queryKey: ['appointments'] }),
    ])
  }

  // Request approval for appointment
  const requestApprovalMutation = useMutation({
    mutationFn: async (appointmentId: string) => {
      const res = await requestApproval(appointmentId)
      if (res?.success === false) throw new Error(res?.message || 'Failed to request approval')
      return res
    },
    onSuccess: async () => {
      toast.success('Approval requested')
      await invalidateAll()
    },
    onError: (err: any) => {
      toast.error('Failed to request approval', { description: err?.message })
    },
  })

  // Approve appointment
  const approveMutation = useMutation({
    mutationFn: async (appointmentId: string) => {
      const res = await approveAppointment(appointmentId)
      if (res?.success === false) throw new Error(res?.message || 'Failed to approve appointment')
      return res
    },
    onSuccess: async () => {
      toast.success('Appointment approved')
      await invalidateAll()
    },
    onError: (err: any) => {
      toast.error('Failed to approve appointment', { description: err?.message })
    },
  })

  // Confirm appointment
  const confirmMutation = useMutation({
    mutationFn: async (appointmentId: string) => {
      const res = await confirmAppointment(appointmentId)
      if (res?.success === false) throw new Error(res?.message || 'Failed to confirm appointment')
      return res
    },
    onSuccess: async () => {
      toast.success('Appointment confirmed')
      await invalidateAll()
    },
    onError: (err: any) => {
      toast.error('Failed to confirm appointment', { description: err?.message })
    },
  })

  // Cancel appointment
  const cancelMutation = useMutation({
    mutationFn: async ({ appointmentId, reason }: { appointmentId: string; reason?: string }) => {
      const res = await cancelAppointment(appointmentId, reason)
      if (res?.success === false) throw new Error(res?.message || 'Failed to cancel appointment')
      return res
    },
    onSuccess: async () => {
      toast.success('Appointment cancelled')
      await invalidateAll()
    },
    onError: (err: any) => {
      toast.error('Failed to cancel appointment', { description: err?.message })
    },
  })

  // Mark no-show
  const markNoShowMutation = useMutation({
    mutationFn: async ({ appointmentId, reason }: { appointmentId: string; reason?: string }) => {
      const res = await markNoShow(appointmentId, reason)
      if (res?.success === false) throw new Error(res?.message || 'Failed to mark no-show')
      return res
    },
    onSuccess: async () => {
      toast.success('Appointment marked as no-show')
      await invalidateAll()
    },
    onError: (err: any) => {
      toast.error('Failed to mark no-show', { description: err?.message })
    },
  })

  // Update appointment
  const updateMutation = useMutation({
    mutationFn: async ({
      appointmentId,
      input,
    }: {
      appointmentId: string
      input: UpdateAppointmentInput
    }) => {
      const res = await updateAppointment(appointmentId, input)
      if (res?.success === false) throw new Error(res?.message || 'Failed to update appointment')
      return res
    },
    onSuccess: async () => {
      toast.success('Appointment updated')
      await invalidateAll()
    },
    onError: (err: any) => {
      toast.error('Failed to update appointment', { description: err?.message })
    },
  })

  return {
    // Action functions
    requestApproval: (appointmentId: string) =>
      requestApprovalMutation.mutateAsync(appointmentId).then(() => {}),
    approveAppointment: (appointmentId: string) =>
      approveMutation.mutateAsync(appointmentId).then(() => {}),
    confirmAppointment: (appointmentId: string) =>
      confirmMutation.mutateAsync(appointmentId).then(() => {}),
    cancelAppointment: (appointmentId: string, reason?: string) =>
      cancelMutation.mutateAsync({ appointmentId, reason }).then(() => {}),
    markNoShow: (appointmentId: string, reason?: string) =>
      markNoShowMutation.mutateAsync({ appointmentId, reason }).then(() => {}),
    updateAppointment: (appointmentId: string, input: UpdateAppointmentInput) =>
      updateMutation.mutateAsync({ appointmentId, input }).then(() => {}),

    // Mutation objects
    requestApprovalMutation,
    approveMutation,
    confirmMutation,
    cancelMutation,
    markNoShowMutation,
    updateMutation,

    // Combined loading state
    isLoading:
      requestApprovalMutation.isPending ||
      approveMutation.isPending ||
      confirmMutation.isPending ||
      cancelMutation.isPending ||
      markNoShowMutation.isPending ||
      updateMutation.isPending,
  }
}
